import { GenericTable } from '@/components/common/tables/common/generic-table/GenericTable';
import { ServiceSold } from '@/pages/protected/Sales/components/services-sold-table/services-sold-tables.types';
import { Badge } from '@/components/ui/badge';
import { statusVariant } from '@/utils/sales';
import { parseDateToDMY } from '@/utils/utils';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type TUserSales = {
  sales: ServiceSold[];
  isLoading: boolean;
};

export const UserSales = ({ sales, isLoading }: TUserSales) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<ServiceSold>[]>(
    () => [
      {
        accessorKey: 'serviceName',
        header: t('clients.sections.clientSales.serviceName'),
        cell: ({ row }) => row.original.service.name || '-'
      },
      {
        accessorKey: 'createdAt',
        header: t('clients.sections.clientSales.createdAt'),
        cell: ({ row }) => parseDateToDMY(row.original.createdAt)
      },
      {
        accessorKey: 'status',
        header: t('clients.sections.clientSales.status.column'),
        cell: ({ row }) => {
          const status = row.original.status.name;

          return (
            <Badge variant={statusVariant[status]}>
              {t(`tables.servicesSold.${status}`)}
            </Badge>
          );
        }
      },
      {
        accessorKey: 'actions',
        header: () => (
          <div className="text-right">
            {t('clients.sections.clientSales.moreInfo')}
          </div>
        ),
        cell: ({ row }) => {
          return (
            <Link
              to={`/sales/${row.original.id}`}
              target="_blank"
              className="flex items-center justify-end gap-2 underline cursor-pointer text-primary"
            >
              {t('clients.sections.clientSales.seeMore')}
              <ExternalLink className="w-4 h-4" />
            </Link>
          );
        }
      }
    ],
    [t]
  );

  const table = useReactTable({
    data: sales,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return <GenericTable table={table} isLoading={isLoading} />;
};
